import type { ComponentProps } from 'react'
import { useRef, useState } from 'react'
import type { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

/**
 * Radix only opens a tooltip from a hovering mouse, and closes it again on
 * pointerdown and on click -- so on a touch screen a tooltip trigger says
 * nothing at all. This makes a tap toggle it instead, and a tap anywhere else
 * close it. The mouse path is untouched: the pointer type is re-read on every
 * move as well as every press, so a mouse on a touchscreen laptop still gets
 * plain hover.
 */
export function useTooltipTap() {
  const [open, setOpen] = useState(false)
  const lastPointerWasTouch = useRef(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const readPointerType = (event: { pointerType: string }) => {
    lastPointerWasTouch.current = event.pointerType !== 'mouse'
  }

  const rootProps: Partial<ComponentProps<typeof Tooltip>> = {
    open,
    onOpenChange: next => {
      // While the last pointer was a finger the tap handler owns the state:
      // Radix's own close requests would undo the very tap that opened it.
      if (!lastPointerWasTouch.current) setOpen(next)
    },
  }

  const triggerProps: Partial<ComponentProps<typeof TooltipTrigger>> = {
    ref: triggerRef,
    onPointerMove: readPointerType,
    onPointerDown: readPointerType,
    onClick: event => {
      if (!lastPointerWasTouch.current) return
      // Skips Radix's close-on-click, which composes after this handler.
      event.preventDefault()
      setOpen(current => !current)
    },
  }

  const contentProps: Partial<ComponentProps<typeof TooltipContent>> = {
    onPointerDownOutside: event => {
      // A tap on the trigger is "outside" the content too, and closing here
      // would let the trigger's own click reopen it.
      const target = event.detail.originalEvent.target
      if (target instanceof Node && triggerRef.current?.contains(target)) return
      setOpen(false)
    },
  }

  return { rootProps, triggerProps, contentProps }
}
